// Building blocks shared by every screen (the web side of TrailBivvy/DesignSystem: Card, primary/secondary actions,
// badges, status views). Plain Preact function components styled by src/ui/styles.css.

import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Icon, type IconName } from './Icon';

export type Tone = 'positive' | 'caution' | 'negative' | 'neutral' | 'unknown' | 'info';

type ButtonVariant = 'primary' | 'secondary' | 'plain' | 'destructive';

export function Button(props: {
  variant?: ButtonVariant;
  icon?: IconName;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  busy?: boolean;
  type?: 'button' | 'submit';
  children: ComponentChildren;
  label?: string;
  id?: string;
}) {
  const className = `btn btn-${props.variant ?? 'secondary'}`;
  const content = (
    <>
      {props.busy ? <span class="spinner" aria-hidden="true" /> : props.icon && <Icon name={props.icon} />}
      <span>{props.children}</span>
    </>
  );
  if (props.href && !props.disabled) {
    const external = /^https?:/.test(props.href);
    return (
      <a class={className} href={props.href} aria-label={props.label} id={props.id} {...(external ? { target: '_blank', rel: 'noopener' } : {})}>
        {content}
      </a>
    );
  }
  return (
    <button
      type={props.type ?? 'button'}
      class={className}
      onClick={props.onClick}
      disabled={props.disabled || props.busy}
      aria-busy={props.busy || undefined}
      aria-label={props.label}
      id={props.id}
    >
      {content}
    </button>
  );
}

export function IconButton(props: { icon: IconName; label: string; onClick?: () => void; href?: string; pressed?: boolean }) {
  if (props.href) {
    return (
      <a class="btn-icon" href={props.href} aria-label={props.label} title={props.label}>
        <Icon name={props.icon} />
      </a>
    );
  }
  return (
    <button type="button" class="btn-icon" onClick={props.onClick} aria-label={props.label} title={props.label} aria-pressed={props.pressed}>
      <Icon name={props.icon} />
    </button>
  );
}

export function Card(props: { children: ComponentChildren; class?: string; labelledBy?: string }) {
  return (
    <section class={`card ${props.class ?? ''}`} aria-labelledby={props.labelledBy}>
      {props.children}
    </section>
  );
}

export function Section(props: { header?: ComponentChildren; footer?: ComponentChildren; children: ComponentChildren; id?: string }) {
  return (
    <section class="section" id={props.id}>
      {props.header && <h2 class="section-header">{props.header}</h2>}
      <div class="section-body">{props.children}</div>
      {props.footer && <p class="section-footer">{props.footer}</p>}
    </section>
  );
}

export function Row(props: {
  icon?: IconName;
  iconTone?: Tone;
  title: ComponentChildren;
  subtitle?: ComponentChildren;
  accessory?: ComponentChildren;
  href?: string;
  onClick?: () => void;
  chevron?: boolean;
  external?: boolean;
  label?: string;
}) {
  const inner = (
    <>
      {props.icon && (
        <span class={`row-icon ${props.iconTone ? `tone-${props.iconTone}` : ''}`}>
          <Icon name={props.icon} />
        </span>
      )}
      <span class="row-text">
        <span class="row-title">{props.title}</span>
        {props.subtitle && <span class="row-subtitle" style={{ display: 'block' }}>{props.subtitle}</span>}
      </span>
      {(props.accessory || props.chevron || props.external) && (
        <span class="row-accessory">
          {props.accessory}
          {props.external ? <Icon name="external" /> : props.chevron && <Icon name="chevronRight" class="chevron" />}
        </span>
      )}
    </>
  );
  if (props.href) {
    const external = props.external || /^https?:|^tel:|^mailto:/.test(props.href);
    return (
      <a class="row" href={props.href} aria-label={props.label} {...(external && /^https?:/.test(props.href) ? { target: '_blank', rel: 'noopener' } : {})}>
        {inner}
      </a>
    );
  }
  if (props.onClick) {
    return (
      <button type="button" class="row" onClick={props.onClick} aria-label={props.label}>
        {inner}
      </button>
    );
  }
  return <div class="row">{inner}</div>;
}

export function Badge(props: { tone: Tone; icon?: IconName; children: ComponentChildren; label?: string }) {
  return (
    <span class={`badge tone-${props.tone}`} aria-label={props.label}>
      {props.icon && <Icon name={props.icon} />}
      {props.children}
    </span>
  );
}

export function Banner(props: { tone: Tone; icon?: IconName; children: ComponentChildren; role?: 'status' | 'alert' }) {
  const icon: IconName = props.icon ?? (props.tone === 'negative' ? 'warning' : props.tone === 'caution' ? 'caution' : 'info');
  return (
    <div class={`banner tone-${props.tone}`} role={props.role}>
      <Icon name={icon} />
      <div>{props.children}</div>
    </div>
  );
}

export function Spinner(props: { label?: string }) {
  return (
    <div class="inline" role="status">
      <span class="spinner" aria-hidden="true" />
      {props.label && <span class="secondary">{props.label}</span>}
    </div>
  );
}

export function EmptyState(props: { icon: IconName; title: ComponentChildren; children?: ComponentChildren }) {
  return (
    <div class="empty-state">
      <Icon name={props.icon} />
      <p class="title3">{props.title}</p>
      {props.children}
    </div>
  );
}

export function NavBar(props: { title: ComponentChildren; back?: { href?: string; onClick?: () => void; label: string }; actions?: ComponentChildren; compact?: boolean }) {
  return (
    <header class={`navbar ${props.compact ? 'compact' : ''}`}>
      {props.back &&
        (props.back.href ? (
          <a class="btn-icon" href={props.back.href} aria-label={props.back.label}>
            <Icon name="chevronLeft" class="chevron" />
          </a>
        ) : (
          <button type="button" class="btn-icon" onClick={props.back.onClick} aria-label={props.back.label}>
            <Icon name="chevronLeft" class="chevron" />
          </button>
        ))}
      <h1>{props.title}</h1>
      {props.actions && <div class="nav-actions">{props.actions}</div>}
    </header>
  );
}

export function Toggle(props: { checked: boolean; onChange: (value: boolean) => void; label: string; id?: string }) {
  return (
    <input
      type="checkbox"
      role="switch"
      class="toggle"
      id={props.id}
      checked={props.checked}
      aria-label={props.label}
      onChange={(e) => props.onChange((e.currentTarget as HTMLInputElement).checked)}
    />
  );
}

export function Segmented<T extends string>(props: { value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <div class="segmented" role="group" aria-label={props.label}>
      {props.options.map((o) => (
        <button type="button" key={o.value} aria-pressed={o.value === props.value} onClick={() => props.onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Modal sheet (dialog semantics, Escape closes, focus moves in and back). */
export function Sheet(props: { title: ComponentChildren; onClose: () => void; children: ComponentChildren; closeLabel: string; actions?: ComponentChildren }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, []);
  const onBackdrop = (e: JSX.TargetedMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) props.onClose();
  };
  return (
    <div class="sheet-backdrop" onClick={onBackdrop}>
      <div class="sheet" role="dialog" aria-modal="true" tabIndex={-1} ref={ref} aria-label={typeof props.title === 'string' ? props.title : undefined}>
        <div class="sheet-header">
          <h2>{props.title}</h2>
          {props.actions}
          <button type="button" class="btn btn-plain" onClick={props.onClose}>
            {props.closeLabel}
          </button>
        </div>
        <div class="sheet-body">{props.children}</div>
      </div>
    </div>
  );
}

export function Figure(props: { value: ComponentChildren; label: ComponentChildren }) {
  return (
    <div>
      <div class="figure-value">{props.value}</div>
      <div class="figure-label">{props.label}</div>
    </div>
  );
}
